import { useState, useRef, useEffect } from 'react';
import styles from './CustomDropdown.module.css';

const CustomDropdown = ({ label, options, value, onChange, placeholder = 'Selecione...', multiple = false }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const containerRef = useRef(null);
  const selectedValues = multiple ? (Array.isArray(value) ? value : []) : [value];
  const hasSelection = multiple ? selectedValues.length > 0 : !!value;

  const isSelected = (option) =>
    selectedValues.some((selected) => String(selected) === String(option));

  const getDisplayValue = () => {
    if (!multiple) return value || placeholder;
    if (!selectedValues.length) return placeholder;
    if (selectedValues.length <= 2) return selectedValues.join(', ');
    return `${selectedValues[0]}, ${selectedValues[1]} +${selectedValues.length - 2}`;
  };

  // Filtra opções pela busca
  const filteredOptions = options.filter(opt =>
    String(opt).toLowerCase().includes(search.toLowerCase())
  );

  // Fecha dropdown ao clicar fora
  useEffect(() => {
    function handleClickOutside(event) {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setOpen(false);
        setSearch('');
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Navegação por teclado
  const handleKeyDown = (e) => {
    if (!open) return;
    if (!filteredOptions.length) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex((prev) => (prev + 1) % filteredOptions.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex((prev) => (prev - 1 + filteredOptions.length) % filteredOptions.length);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      handleSelect(filteredOptions[highlightedIndex]);
    } else if (e.key === 'Escape') {
      setOpen(false);
      setSearch('');
    }
  };

  // Seleciona uma opção
  const handleSelect = (option) => {
    if (multiple) {
      const alreadySelected = isSelected(option);
      const nextValue = alreadySelected
        ? selectedValues.filter((selected) => String(selected) !== String(option))
        : [...selectedValues, option];
      onChange(nextValue);
      return;
    }

    onChange(option);
    setOpen(false);
    setSearch('');
  };

  return (
    <div className={styles.container} ref={containerRef}>
      <label className={styles.label}>{label}</label>
      <div
        tabIndex={0}
        className={styles.control}
        onClick={() => setOpen(!open)}
        onKeyDown={handleKeyDown}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className={hasSelection ? styles.value : styles.placeholder}>
          {getDisplayValue()}
        </span>
        <span className={styles.arrow}>&#9662;</span>
      </div>
      {open && (
        <div className={styles.dropdown} role="listbox" aria-multiselectable={multiple}>
          <input
            type="text"
            className={styles.search}
            placeholder="Digite para buscar..."
            value={search}
            onChange={e => { setSearch(e.target.value); setHighlightedIndex(0); }}
            autoFocus
            aria-label="Buscar opções"
          />
          <div className={styles.options}>
            {filteredOptions.length > 0 ? (
              filteredOptions.map((opt, i) => (
                <div
                  key={`${opt}-${i}`}
                  role="option"
                  aria-selected={isSelected(opt)}
                  className={`${styles.option} ${i === highlightedIndex ? styles.highlighted : ''} ${isSelected(opt) ? styles.selected : ''}`}
                  onClick={() => handleSelect(opt)}
                  onMouseEnter={() => setHighlightedIndex(i)}
                >
                  <span className={styles.optionContent}>
                    {multiple && (
                      <span className={styles.optionCheck}>{isSelected(opt) ? '[x]' : '[ ]'}</span>
                    )}
                    <span>{opt}</span>
                  </span>
                </div>
              ))
            ) : (
              <div className={styles.noOptions}>Nenhuma opção encontrada</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomDropdown;
